import {
  FeatureService,
  FeaturesResult,
  UpdateWorkspaceRequest,
  WorkspaceResult,
  WorkspaceService,
} from '@/api/';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '@nanostores/react';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Loader2,
  Pencil,
  Trash2Icon,
  User2Icon,
  Users2Icon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DeleteWorkspaceConfirmation } from './DeleteWorkspaceConfirmation';

import { ReactNode } from 'react';

function SortableItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function handleViewProjects(orgId: string, ws: WorkspaceResult) {
  $workspace.set(ws);

  openPage($router, 'projects', {
    organizationId: orgId,
    workspaceId: ws.workspace.id,
  });
}

function handleViewWorkflows(orgId: string, ws: WorkspaceResult) {
  $workspace.set(ws);

  openPage($router, 'workflows', {
    organizationId: orgId,
    workspaceId: ws.workspace.id,
  });
}

function handleManageCollaborators(orgId: string, ws: WorkspaceResult) {
  $workspace.set(ws);

  openPage($router, 'workspaceCollaborators', {
    organizationId: orgId,
    workspaceId: ws.workspace.id,
  });
}

export type WorkspaceListingProps = {
  organizationId: string;
};

export const WorkspaceListing = ({ organizationId }: WorkspaceListingProps) => {
  const org = useStore($org);
  const user = useStore($user);
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const [features, setFeatures] = useState<FeaturesResult[]>([]); // Define the state type

  useEffect(() => {
    const fetchFeatures = async () => {
      const orgId = org?.id;
      if (orgId) {
        const fetchedFeatures = await FeatureService.getFeatures(orgId);
        setFeatures(fetchedFeatures);
      }
    };

    fetchFeatures();
  }, [org]);

  const isWorkflowsEnabled = features.some(
    feature => feature.name === 'Workflows' && feature.enabled,
  );

  const [isDeleteWorkspaceModalOpen, setIsDeleteWorkspaceModalOpen] =
    useState(false);
  const [workspaceToBeDeleted, setWorkspaceToBeDeleted] = useState('');

  const [editingWorkspaceName, setEditingWorkspaceName] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');

  const workspaces = useQuery<WorkspaceResult[]>({
    queryKey: ['getWorkspaces', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await WorkspaceService.getWorkspaces(organizationId);
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  function constructOrderMap(orders: string[]): Map<string, number> {
    const orderMap = new Map<string, number>();
    orders.forEach((item, index) => {
      orderMap.set(item, index);
    });
    return orderMap;
  }

  function sortWorkspacesUsingSavedOrders(
    items: WorkspaceResult[],
  ): WorkspaceResult[] {
    // console.log('Attempting to sort using saved orders for org:', organizationId);
    const savedWorkspaceOrdersRaw = localStorage.getItem('workspaces_orders');
    // console.log('localStorage raw:', savedWorkspaceOrdersRaw);
    const savedWorkspaceOrders = JSON.parse(savedWorkspaceOrdersRaw || '{}');
    // console.log('savedWorkspaceOrders parsed:', savedWorkspaceOrders);

    if (Object.keys(savedWorkspaceOrders)?.includes(organizationId)) {
      const workspaceOrder = savedWorkspaceOrders[organizationId];
      // console.log('Found saved order for org:', organizationId, workspaceOrder);
      const workspaceOrderMap = constructOrderMap(workspaceOrder);
      const sortedItems = items.sort((a, b) => {
        const orderA =
          workspaceOrderMap.get(a?.workspace?.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB =
          workspaceOrderMap.get(b?.workspace?.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      // console.log('Sorted items according to saved order:', sortedItems.map(i => i.workspace.id));
      return sortedItems;
    }

    // console.log('No saved order found for org:', organizationId, 'returning items as is.');
    return items;
  }

  const updateWorkspace = useMutation({
    mutationFn: async (payload: UpdateWorkspaceRequest) => {
      if (!workspaceToBeDeleted) {
        throw new Error('No workspace selected for update.');
      }

      const res = await WorkspaceService.updateWorkspace(
        workspaceToBeDeleted,
        payload,
      );
      return res;
    },
    onSuccess: async data => {
      if (!data?.workspace) {
        console.error('Error: data.workspace is undefined.');
        return;
      }

      $workspace.set({
        ...(selectedWs || { invites: 0, collaborators: [], projects: [] }),
        workspace: data.workspace,
      });

      setEditingWorkspaceName(false);

      await queryClient.invalidateQueries({
        queryKey: ['getWorkspaces', organizationId],
      });
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: (error as Error).message,
        duration: 5000,
      });
    },
  });

  const selectedWs = useStore($workspace);

  // Local state for ordered workspaces
  const [orderedWorkspaces, setOrderedWorkspaces] = useState<WorkspaceResult[]>(
    [],
  );

  // Always sort and set orderedWorkspaces after we have workspaces data
  useEffect(() => {
    // console.log('useEffect triggered, orderedWorkspaces length:', orderedWorkspaces.length);
    // console.log('workspaces.isSuccess:', workspaces.isSuccess, 'workspaces.data:', workspaces.data?.map(w => w.workspace.id));

    if (
      workspaces?.isSuccess &&
      workspaces?.data &&
      workspaces.data.length > 0
    ) {
      // console.log('Sorting from scratch on every load...');
      // Always sort using local storage
      const sorted = sortWorkspacesUsingSavedOrders(workspaces.data);

      setOrderedWorkspaces(sorted);
      // console.log('Set orderedWorkspaces after load:', sorted.map(w => w.workspace.id));

      $workspaces.set(sorted);
      // console.log('Set $workspaces to sorted order:', sorted.map(w => w.workspace.id));
    } else {
      // console.log('No data to sort or loading not complete yet.');
    }
  }, [workspaces.isSuccess, workspaces.data, organizationId]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    // console.log('Drag ended: active=', active.id, 'over=', over.id);

    if (active.id !== over.id) {
      const oldIndex = orderedWorkspaces.findIndex(
        item => item.workspace.id === active.id,
      );
      const newIndex = orderedWorkspaces.findIndex(
        item => item.workspace.id === over.id,
      );
      // console.log('Reordering: oldIndex=', oldIndex, 'newIndex=', newIndex);

      const newOrderedWorkspaces = arrayMove(
        orderedWorkspaces,
        oldIndex,
        newIndex,
      );
      // console.log('New order after drag:', newOrderedWorkspaces.map(w => w.workspace.id));
      setOrderedWorkspaces(newOrderedWorkspaces);

      const savedWorkspaceOrdersRaw = localStorage.getItem('workspaces_orders');
      const savedWorkspaceOrders = JSON.parse(savedWorkspaceOrdersRaw || '{}');
      // console.log('Before saving new order to localStorage:', savedWorkspaceOrders);

      if (!savedWorkspaceOrders[organizationId]) {
        savedWorkspaceOrders[organizationId] = [];
      }

      savedWorkspaceOrders[organizationId] = newOrderedWorkspaces.map(
        ws => ws.workspace.id,
      );

      localStorage.setItem(
        'workspaces_orders',
        JSON.stringify(savedWorkspaceOrders),
      );
      // console.log('Saved new order to localStorage:', savedWorkspaceOrders);

      $workspaces.set(newOrderedWorkspaces);
      // console.log('Updated $workspaces store:', newOrderedWorkspaces.map(w => w.workspace.id));
    } else {
      console.log('No order change on drag end.');
    }
  };

  return (
    <>
      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="flex flex-col gap-2 pr-4">
          {workspaces.isLoading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {workspaces.data?.length === 0 && (
            <div className="flex h-32 items-center justify-center">
              <p>No workspaces found.</p>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedWorkspaces.map(ws => ws.workspace.id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedWorkspaces.map(ws => (
                <SortableItem key={ws.workspace.id} id={ws.workspace.id}>
                  <Card
                    className={cn(
                      selectedWs?.workspace.id === ws.workspace.id &&
                        'ring-primary-muted ring-1 ring-inset',
                    )}
                    key={ws.workspace?.id}
                  >
                    <CardHeader className="flex flex-row gap-4 space-y-0">
                      <div className="flex flex-grow items-center gap-2">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={ws.workspace.icon || ''} />
                          <AvatarFallback>
                            {ws.workspace.name.slice(0, 2).toLocaleUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1">
                          {editingWorkspaceName &&
                          workspaceToBeDeleted === ws.workspace.id ? (
                            <Input
                              value={workspaceName}
                              onChange={e => setWorkspaceName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  // console.log('Editing workspace name on Enter');
                                  updateWorkspace.mutateAsync({
                                    name: workspaceName,
                                  });
                                }
                              }}
                              className="w-full"
                            />
                          ) : (
                            <CardTitle>{ws.workspace.name}</CardTitle>
                          )}
                          <CardDescription>
                            {ws.workspace.description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-none gap-2">
                        <Button
                          variant="outline"
                          className=""
                          onClick={() =>
                            handleViewProjects(org?.id ?? 'select-an-org', ws)
                          }
                        >
                          Projects
                        </Button>

                        {/* {user && user.uid && isWorkflowsEnabled && (
                          <Button
                            variant="outline"
                            className=""
                            onClick={() =>
                              handleViewWorkflows(
                                org?.id ?? 'select-an-org',
                                ws,
                              )
                            }
                          >
                            Workflows
                          </Button>
                        )} */}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon">
                              <DotsHorizontalIcon className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-[200px]"
                            forceMount
                          >
                            <DropdownMenuLabel>Options</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleManageCollaborators(
                                  org?.id ?? 'select-an-org',
                                  ws,
                                )
                              }
                            >
                              <Users2Icon className="mr-2 h-4 w-4" /> Manage
                              Collaborators
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                // console.log('Editing workspace triggered');
                                setEditingWorkspaceName(true);
                                setWorkspaceToBeDeleted(ws.workspace.id);
                                setWorkspaceName(ws.workspace.name);
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" /> Edit Workspace
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                // console.log('Delete workspace triggered');
                                setWorkspaceToBeDeleted(ws.workspace.id);
                                setIsDeleteWorkspaceModalOpen(true);
                              }}
                            >
                              <Trash2Icon className="mr-2 h-4 w-4" /> Delete
                              Workspace
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    {editingWorkspaceName &&
                      workspaceToBeDeleted === ws.workspace.id && (
                        <div className="flex justify-end gap-2 p-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // console.log('Cancel editing workspace name');
                              setEditingWorkspaceName(false);
                              setWorkspaceName(ws.workspace.name);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // console.log('Save edited workspace name:', workspaceName);
                              updateWorkspace.mutateAsync({
                                name: workspaceName,
                              });
                            }}
                          >
                            {updateWorkspace.isPending ? (
                              <Loader2 className="ml-0 h-3 animate-spin p-0" />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </div>
                      )}
                    <CardContent>
                      <div className="flex space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <Users2Icon className="mr-1 h-3 w-3" />
                          {ws.collaborators.length} Collaborators
                        </div>
                        {ws.workspace.createdBy && (
                          <div className="flex items-center">
                            <User2Icon className="mr-1 h-3 w-3" />
                            Created by: {ws.workspace.createdBy.name || 'N/A'}
                          </div>
                        )}

                        <div className="flex items-center">
                          <Calendar className="mr-1 h-3 w-3" />
                          Created on{' '}
                          {new Date(
                            ws.workspace.createdAtUtc,
                          ).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <DeleteWorkspaceConfirmation
          isOpen={isDeleteWorkspaceModalOpen}
          handleOpen={setIsDeleteWorkspaceModalOpen}
          workspaceId={workspaceToBeDeleted}
          organizationId={organizationId}
        />
      </ScrollArea>
    </>
  );
};
